// The app's own confirm dialog (aithlo-frontend#34), in place of the native
// window.confirm()/prompt() that froze the whole tab and read
// "aithlo.com says…". <ConfirmProvider> is mounted once in App.jsx;
//
//   const confirm = useConfirm();
//   if (!(await confirm({ title: 'Delete this workout?', confirmText: 'Delete', destructive: true }))) return;
//
// resolves true on confirm, false on cancel / Escape / a click outside.
// `requireText` asks the user to type a word (an email, DELETE) before the
// confirm button enables — the old window.prompt() double-checks.
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '../../lib/utils';
import { ConfirmDialogView } from '../ds/ConfirmDialog';

const ConfirmContext = createContext(async () => {
  console.error('useConfirm() used outside <ConfirmProvider>; treating it as cancelled.');
  return false;
});

/** `confirm(options) => Promise<boolean>`. */
export const useConfirm = () => useContext(ConfirmContext);

export function ConfirmView({ open, options, typed, onTyped, onAnswer }) {
  const o = options || {};
  const needs = o.requireText ? typed.trim().toLowerCase() === String(o.requireText).trim().toLowerCase() : true;
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => { if (!next) onAnswer(false); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[10020] bg-black/50" />
        <DialogPrimitive.Content role="alertdialog"
          className="fixed left-1/2 top-1/2 z-[10021] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-bg-surface p-6 shadow-xl focus:outline-none">
          <DialogPrimitive.Title className="m-0 text-base font-semibold text-text-primary">{o.title}</DialogPrimitive.Title>
          {o.description ? (
            <DialogPrimitive.Description className="m-0 mt-2 text-sm leading-relaxed text-text-secondary">{o.description}</DialogPrimitive.Description>
          ) : (
            <DialogPrimitive.Description className="sr-only">{o.title}</DialogPrimitive.Description>
          )}
          <form onSubmit={(e) => { e.preventDefault(); if (needs) onAnswer(true); }}>
            {o.requireText && (
              <label className="mt-4 block text-sm text-text-body">
                {o.requireLabel || <>Type <span className="font-mono font-semibold">{o.requireText}</span> to confirm.</>}
                <input autoFocus value={typed} onChange={(e) => onTyped(e.target.value)}
                  autoComplete="off" spellCheck={false}
                  className="mt-2 block w-full rounded-lg border border-border-strong px-3 py-2 text-sm focus:border-accent-default focus:outline-none focus:ring-2 focus:ring-accent-default" />
              </label>
            )}
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              {!o.alert && (
                <button type="button" onClick={() => onAnswer(false)}
                  className="min-h-[40px] rounded-full border border-border-strong px-4 text-sm font-medium text-text-body hover:bg-bg-raised">
                  {o.cancelText || 'Cancel'}
                </button>
              )}
              <button type="submit" disabled={!needs} autoFocus={!o.requireText}
                className={cn('min-h-[40px] rounded-full px-4 text-sm font-semibold text-white disabled:opacity-50',
                  o.destructive ? 'bg-status-danger hover:bg-status-danger' : 'bg-accent-default hover:bg-accent-hover')}>
                {o.confirmText || 'OK'}
              </button>
            </div>
          </form>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, options: null });
  const [typed, setTyped] = useState('');
  const resolver = useRef(null);

  const confirm = useCallback((options) => new Promise((resolve) => {
    resolver.current?.(false);                 // a second ask cancels the first
    resolver.current = resolve;
    setTyped('');
    setState({ open: true, options: typeof options === 'string' ? { title: options } : options });
  }), []);

  const answer = useCallback((value) => {
    resolver.current?.(value);
    resolver.current = null;
    setState((s) => ({ ...s, open: false }));
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialogView open={state.open} options={state.options} typed={typed} onTyped={setTyped} onAnswer={answer} />
    </ConfirmContext.Provider>
  );
}

export default ConfirmProvider;
